import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, PackageCheck, ClipboardCheck, ShieldCheck, Trash2, Search, MapPin, Download, Upload } from "lucide-react";
import DateField from "@/components/DateField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import FrameworkEmailDialog from "../../components/FrameworkEmailDialog";
import IssueDialog from "./IssueDialog";
import AckDialog from "./AckDialog";
import ReceiveDialog from "./ReceiveDialog";
import QuoteDialog, { QUOTE_TONE } from "./QuoteDialog";
import ReviewGate from "./ReviewGate";
import ReviewFindings from "./ReviewFindings";
import Hint from "@/components/Hint";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;
const SHIP_TO = [["", "Choose…"], ["site", "Ship to site"], ["abs_broomfield", "Ship to ABS Broomfield"],
  ["abs_colorado_springs", "Ship to ABS Colorado Springs"]];

export default function PoDetail({ project, poId, canEdit, canIssue, onChanged }) {
  const [po, setPo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [issuing, setIssuing] = useState(false);
  const [acking, setAcking] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [gateFor, setGateFor] = useState(null);       // "issue" | "email" — PRC-REV gate in front
  const [review, setReview] = useState(null);

  const load = useCallback(async () => {
    try {
      const p = await opsApi.po(project.id, poId);
      setPo(p);
      if (p.review_state !== "not_run" || p.review_skipped) {
        opsApi.poReview(project.id, poId).then(setReview).catch(() => setReview(null));
      }
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load that purchase order"); }
  }, [project.id, poId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (data) => {
    setBusy(true);
    try {
      setPo(await opsApi.poPatch(project.id, poId, data));
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be saved"); }
    finally { setBusy(false); }
  };

  const emailVendor = async () => {
    setGateFor(null);
    setBusy(true);
    try { setDraft(await opsApi.emailDraft(project.id, "purchase_order", poId)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not prepare the vendor email"); }
    finally { setBusy(false); }
  };

  const voidPo = async () => {
    if (!window.confirm(`Void ${po.po_number}? The commitment is released and the FRS purchase `
      + "status is cleared. The number is never reused.")) return;
    setBusy(true);
    try {
      const res = await opsApi.poVoid(project.id, poId);
      toast.success(res.discarded ? `${res.discarded} discarded` : `${res.voided} voided`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be voided"); }
    finally { setBusy(false); }
  };

  if (!po) {
    return <div className="flex items-center gap-2 border-t border-slate-100 px-4 py-6 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div>;
  }

  const editable = canEdit && !["void", "closed"].includes(po.status);
  const gated = !po.sent_at && ["prepared", "issued"].includes(po.status);

  return (
    <div className="border-t border-slate-100 px-4 py-3" data-testid={`ops-po-detail-${po.po_number}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs" data-testid="prc-po-state">
        <Search className="h-3.5 w-3.5 text-slate-400" />
        <span className="text-slate-500">Procurement review</span>
        {po.review_high_unacknowledged > 0 && (
          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] text-rose-700" title="High findings still unacknowledged">
            {po.review_high_unacknowledged} high open
          </span>
        )}
        <span className="text-slate-400" title={review?.mode === "auto"
          ? `Runs automatically before issue/email — this PO is at or above the $${Number(review.threshold || 0).toLocaleString()} threshold`
          : `Below the $${Number(review?.threshold || 0).toLocaleString()} threshold — you are asked before issue/email`}>
          {review?.mode === "auto" ? "auto · runs before issue" : "asks before issue"}
        </span>
        {canIssue && po.status !== "void" && (
          <Button size="sm" variant="data" className="ml-auto h-7 text-xs" onClick={() => setGateFor("review")}
                  data-testid="prc-po-run"><Search className="mr-1.5 h-3.5 w-3.5" />{po.review_state === "not_run" ? "Review" : "Re-review"}</Button>
        )}
      </div>
      {review?.review && (
        <div className="mb-3 rounded-lg border border-slate-200 p-3" data-testid="prc-po-panel">
          <ReviewFindings gate={review} project={project} poId={po.id} canAck={canIssue}
                          onChanged={(g) => { setReview(g); load(); onChanged?.(); }} />
        </div>
      )}
      <div className="grid gap-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="text-slate-500">Ship to</span>
          <select value={po.ship_to || ""} disabled={!editable}
                  onChange={(e) => patch({ ship_to: e.target.value })}
                  data-testid="ops-po-ship-to"
                  className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs">
            {SHIP_TO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {po.ship_to === "site" && (
            <span className="mt-1 flex items-start gap-1 text-[11px] text-sky-800 dark:text-sky-300" data-testid="ops-po-site-address">
              <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{[project.address, project.city, project.state].filter(Boolean).join(", ") || "No site address on the job yet"}<br />
                <span className="text-slate-500">Receiving creates a direct-ship delivery for the field — never shop stock</span></span>
            </span>
          )}
        </label>
        <div className="block">
          <span className="text-slate-500"><Hint text={`Defaults to today + the ${po.manufacturer} lead time (${po.lead_time_days} days) — override as needed`}>Required by</Hint></span>
          <div className="mt-1"><DateField value={po.required_by || po.order_by} disabled={!editable} testId="ops-po-required-by"
                     onChange={(v) => patch({ required_by: v })} /></div>
        </div>
        <div className="block">
          <span className="text-slate-500"><Hint text="Actual ship date from the vendor's ship notice (purchasing@ inbox) — set by hand if it came another way">Shipped on</Hint></span>
          <div className="mt-1"><DateField value={po.shipped_on || ""} disabled={!editable || ["prepared"].includes(po.status)} testId="ops-po-shipped-on"
                     onChange={(v) => patch({ shipped_on: v })} /></div>
          {(po.carrier || po.tracking) && <span className="text-[11px] text-slate-500" data-testid="ops-po-tracking">{[po.carrier, po.tracking].filter(Boolean).join(" · ")}</span>}
        </div>
        <div className="block">
          <span className="text-slate-500"><Hint text="Set automatically to the day the PO was emailed to the vendor — change it only if it was ordered another way">Ordered on</Hint></span>
          <div className="mt-1"><DateField value={po.ordered_on || (po.sent_at || "").slice(0, 10)} disabled={!editable} testId="ops-po-ordered-on"
                     onChange={(v) => patch({ ordered_on: v })} /></div>
          {!po.sent_at && !po.ordered_on_manual && <span className="text-[11px] text-slate-400">fills in when the PO is emailed</span>}
        </div>
        <label className="block">
          <span className="text-slate-500">Freight</span>
          <select value={po.freight_mode || "prepaid"} disabled={!editable}
                  onChange={(e) => patch({ freight_mode: e.target.value })}
                  data-testid="ops-po-freight-mode"
                  className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs">
            <option value="prepaid">Prepaid</option>
            <option value="amount">Quoted</option>
          </select>
        </label>
        {po.freight_mode === "amount" && (
          <label className="block">
            <span className="text-slate-500">Quoted freight</span>
            <Input type="number" defaultValue={po.freight_amount} disabled={!editable}
                   onBlur={(e) => Number(e.target.value) !== po.freight_amount
                     && patch({ freight_amount: Number(e.target.value) })}
                   className="mt-1 h-8 text-xs" data-testid="ops-po-freight-amount" />
          </label>
        )}
        <label className="block">
          <span className="text-slate-500">Ship via</span>
          <Input defaultValue={po.ship_via} disabled={!editable}
                 onBlur={(e) => e.target.value !== po.ship_via && patch({ ship_via: e.target.value })}
                 className="mt-1 h-8 text-xs" data-testid="ops-po-ship-via" />
        </label>
        <div>
          <span className="text-slate-500">Vendor order #</span>
          <p className="mt-1.5 font-medium text-slate-800" data-testid="ops-po-vendor-order">
            {po.vendor_order_number || "—"}
          </p>
        </div>
      </div>

      {(po.expected_ship_date || po.current_ship_date) && (
        <p className="mt-2 text-xs text-slate-600" data-testid="ops-po-ship-dates">
          Ship date: {(po.expected_ship_date || "—").slice(0, 10)}
          {po.current_ship_date && po.current_ship_date !== po.expected_ship_date
            && ` → ${po.current_ship_date.slice(0, 10)}`}
          {po.ship_slipped && <span className="ml-1 font-medium text-amber-700">(slipped)</span>}
        </p>
      )}

      {po.quotes?.length > 0 && (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid="ops-po-quote-strip">
          <span>Vendor quote:</span>
          {po.quote_ref ? (
            <span className={`rounded-full px-2 py-0.5 font-medium ${QUOTE_TONE.applied}`}>Applied {po.quote_ref}
              {po.quote_valid_through && ` · valid through ${po.quote_valid_through.slice(0, 10)}`}</span>
          ) : (
            <span className={`rounded-full px-2 py-0.5 font-medium ${QUOTE_TONE[po.quotes[0].status] || ""}`}>
              {po.quotes[0].quote_ref || "latest"} · {po.quotes[0].status}</span>
          )}
          {po.quote_counts?.received > 0 && !po.quote_ref && <span className="text-sky-800">{po.quote_counts.received} waiting for review</span>}
          {po.quote_counts?.expired > 0 && <span className="text-amber-700">{po.quote_counts.expired} expired</span>}
        </p>
      )}

      {po.authorization?.note && (
        <p className="mt-2 rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-800"
           data-testid="ops-po-authorization">
          Issued on a not-Open project with written authorization: “{po.authorization.note}” —
          {po.authorization.by}
        </p>
      )}

      <table className="mt-3 w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-2 py-1.5 text-left">Part</th>
            <th className="px-2 py-1.5 text-left">Description</th>
            <th className="px-2 py-1.5 text-left">Cost code</th>
            <th className="px-2 py-1.5 text-right">Qty</th>
            <th className="px-2 py-1.5 text-right">Received</th>
            <th className="px-2 py-1.5 text-right">Unit</th>
            <th className="px-2 py-1.5 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {po.lines.map((l) => (
            <tr key={l.id} data-testid={`ops-po-line-${l.id}`}>
              <td className="px-2 py-1 font-mono">{l.item_number || "—"}</td>
              <td className="px-2 py-1">
                {l.description}
                {l.source === "co" && <span className="ml-1 rounded bg-violet-100 px-1 text-[10px] text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" title="This line comes from a processed change order">CO</span>}
                {l.source === "freight" && <span className="ml-1 text-slate-400">· freight</span>}
              </td>
              <td className="px-2 py-1 text-slate-500">
                <span className="font-mono">{l.cost_code || "—"}</span>
                {l.cost_type && <span className="ml-1">· {l.cost_type_name || l.cost_type}</span>}
              </td>
              <td className="px-2 py-1 text-right">{Number(l.qty).toLocaleString()}</td>
              <td className="px-2 py-1 text-right">
                {l.received_qty ? Number(l.received_qty).toLocaleString() : "—"}
              </td>
              <td className="px-2 py-1 text-right">{money(l.unit_cost)}</td>
              <td className="px-2 py-1 text-right">{money(l.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-1.5 text-right text-sm font-semibold text-slate-900"
         data-testid="ops-po-total">Total {money(po.total)}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="data" className="text-xs" data-testid="ops-po-pdf" disabled={busy}
                onClick={() => opsApi.poPdf(project.id, po.id)
                  .catch(() => toast.error("That PDF could not be opened"))}>
          <Download className="mr-1.5 h-3.5 w-3.5" /> PDF
        </Button>
        {po.status === "prepared" && canIssue && (
          <Button size="sm" variant="data" className="text-xs" onClick={() => setGateFor("issue")} disabled={busy}
                  data-testid="ops-po-issue">
            <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Issue
          </Button>
        )}
        {["prepared", "issued", "ordered", "shipped", "partially_received"].includes(po.status) && canIssue && (
          <Button size="sm" variant="data" className="text-xs" onClick={() => (gated ? setGateFor("email") : emailVendor())} disabled={busy}
                  data-testid="ops-po-email">
            <Mail className="mr-1.5 h-3.5 w-3.5" /> Email
          </Button>
        )}
        {["issued", "ordered", "shipped", "partially_received"].includes(po.status) && canEdit && (
          <Button size="sm" variant="data" className="text-xs" onClick={() => setAcking(true)} disabled={busy}
                  data-testid="ops-po-ack" title="Record the vendor's acknowledgement">
            <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" /> Acknowledge
          </Button>
        )}
        {["issued", "ordered", "shipped", "partially_received"].includes(po.status) && canEdit && (
          <Button size="sm" variant="data" className="text-xs" onClick={() => setReceiving(true)} disabled={busy}
                  data-testid="ops-po-receive">
            <PackageCheck className="mr-1.5 h-3.5 w-3.5" /> Receive
          </Button>
        )}
        {po.status !== "void" && (
          <Button size="sm" variant="data" className="text-xs" onClick={() => setQuoting(true)} disabled={busy}
                  data-testid="ops-po-quotes" title="Upload / compare vendor quotes for this PO">
            <Upload className="mr-1.5 h-3.5 w-3.5" /> Quotes
            {po.quotes?.length > 0 && <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 text-[10px]">{po.quotes.length}</span>}
          </Button>
        )}
        {editable && !po.lines.some((l) => l.received_qty) && (
          <Button size="sm" variant="ghost" onClick={voidPo} disabled={busy}
                  data-testid="ops-po-void" className="text-xs text-slate-500 hover:text-rose-700">
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            {po.status === "prepared" ? "Discard" : "Void"}
          </Button>
        )}
        {po.acks?.length > 0 && (
          <span className="ml-auto text-xs text-slate-500" data-testid="ops-po-ack-count">
            {po.acks.length} acknowledgement event(s)
            {po.acks[0].discrepancies?.length
              ? ` · ${po.acks[0].discrepancies.length} discrepancy(ies)` : ""}
          </span>
        )}
      </div>

      {gateFor && (
        <ReviewGate project={project} po={po} action={gateFor}
                    onClose={() => { setGateFor(null); load(); onChanged?.(); }}
                    onProceed={() => {
                      if (gateFor === "issue") { setGateFor(null); setIssuing(true); }
                      else if (gateFor === "email") emailVendor();
                      else { setGateFor(null); load(); onChanged?.(); }
                    }} />
      )}
      {issuing && (
        <IssueDialog project={project} po={po} onClose={() => setIssuing(false)}
                     onDone={() => { setIssuing(false); load(); onChanged?.(); }} />
      )}
      {acking && (
        <AckDialog project={project} po={po} onClose={() => setAcking(false)}
                   onDone={() => { setAcking(false); load(); onChanged?.(); }} />
      )}
      {quoting && (
        <QuoteDialog project={project} po={po} canEdit={editable} onClose={() => { setQuoting(false); load(); }}
                     onChanged={() => { load(); onChanged?.(); }} />
      )}
      {receiving && (
        <ReceiveDialog project={project} po={po} onClose={() => setReceiving(false)}
                       onDone={() => { setReceiving(false); load(); onChanged?.(); }} />
      )}
      {draft && (
        <FrameworkEmailDialog projectId={project.id} draft={draft}
                              onClose={() => setDraft(null)}
                              onSent={() => { setDraft(null); load(); onChanged?.(); }} />
      )}
    </div>
  );
}
